/*
 * Purpose: AI + rule-based Mission flow summary for builder side panel
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { z } from 'zod';
import type { Node, Workflow } from '@/stores/workflowStore';
import { getNodeLabel } from '@/lib/workflowUtils';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import { getMissionGoalIntent, getNodePurposeLine } from '@/lib/mapNodeSummary';
import { buildGoalCentricMapContext } from '@/lib/mapAssistantContext';
import { hasResolvedSecret, resolveSecret } from '@/lib/resolveSecret';
import { readEnvOr } from '@/lib/runtimeEnv';

const MODEL = readEnvOr('VITE_OPENAI_MODEL', 'gpt-4o-mini');

export type FlowSummaryAction = {
  order: number;
  label: string;
  detail: string;
  blockType: string;
};

export type FlowSummary = {
  intent: string;
  whatItDoes: string;
  actions: FlowSummaryAction[];
  source: 'ai' | 'rules';
};

const flowSummarySchema = z.object({
  intent: z.string().min(1),
  whatItDoes: z.string().min(1),
  actions: z
    .array(
      z.object({
        label: z.string().min(1),
        detail: z.string().min(1),
      })
    )
    .min(1)
    .max(12),
});

/** Semantic map fingerprint — ignores layout (x/y), reacts to blocks, config, and links */
export function workflowGraphFingerprint(workflow: Workflow): string {
  const nodePart = [...workflow.nodes]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((n) => {
      const cfg = n.config ?? {};
      const semantic = {
        outcome: cfg.outcome,
        name: cfg.name,
        message: cfg.message,
        contextPurpose: cfg.contextPurpose,
        agentBrief: cfg.agentBrief,
        droneTask: cfg.droneTask,
        _autoBlocksRationale: cfg._autoBlocksRationale,
        gmailQuery: cfg.gmailQuery,
        source: cfg.source,
        url: cfg.url,
        method: cfg.method,
      };
      return `${n.id}:${n.type}.${n.subtype}:${JSON.stringify(semantic)}`;
    })
    .join('|');

  const edgePart = [...workflow.edges]
    .sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to))
    .map((e) => `${e.from}->${e.to}:${e.kind ?? 'link'}`)
    .join('|');

  return `${workflow.id}#${workflow.name.trim()}#n[${nodePart}]#e[${edgePart}]`;
}

const summaryCache = new Map<string, FlowSummary>();

export function flowSummaryCacheKey(workflow: Workflow): string {
  return workflowGraphFingerprint(workflow);
}

function orderedFlowNodes(workflow: Workflow): Node[] {
  const { nodes, edges } = workflow;
  if (nodes.length === 0) return [];

  const outEdges = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  for (const node of nodes) {
    outEdges.set(node.id, []);
    inDegree.set(node.id, 0);
  }
  for (const edge of edges) {
    outEdges.get(edge.from)?.push(edge.to);
    inDegree.set(edge.to, (inDegree.get(edge.to) ?? 0) + 1);
  }

  const startId =
    nodes.find((n) => n.type === 'start')?.id ??
    nodes.find((n) => (inDegree.get(n.id) ?? 0) === 0)?.id ??
    nodes[0].id;

  const order: Node[] = [];
  const seen = new Set<string>();
  const queue = [startId];

  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = nodes.find((n) => n.id === id);
    if (node) order.push(node);
    for (const next of outEdges.get(id) ?? []) {
      if (!seen.has(next)) queue.push(next);
    }
  }

  for (const node of nodes) {
    if (!seen.has(node.id)) order.push(node);
  }

  return order;
}

export function buildRuleBasedFlowSummary(workflow: Workflow): FlowSummary {
  const intent = getMissionGoalIntent(workflow);
  const ordered = orderedFlowNodes(workflow);

  const actions: FlowSummaryAction[] = ordered
    .filter((n) => n.type !== 'start')
    .map((node, index) => ({
      order: index + 1,
      label: getNodeLabel(node),
      detail: getNodePurposeLine(node),
      blockType: getBlockTypeLabel(node.type, node.subtype),
    }));

  if (actions.length === 0) {
    actions.push({
      order: 1,
      label: 'Plan your flow',
      detail: 'Add blocks on the map or ask the Map AI Assistant to wire steps toward your goal.',
      blockType: 'Mission',
    });
  }

  const trigger = ordered.find((n) => n.type === 'trigger');
  const operational = ordered.filter(
    (n) => n.type === 'trigger' || n.type === 'tool' || n.type === 'action' || n.type === 'agent' || n.type === 'drone'
  );

  let whatItDoes: string;
  if (operational.length === 0) {
    whatItDoes =
      'This map defines your Mission outcome. Connect triggers and actions from START through GOAL to automate the work.';
  } else if (trigger) {
    const triggerLabel = getNodeLabel(trigger);
    const stepCount = operational.length;
    whatItDoes = `Starts when ${triggerLabel} runs, then executes ${stepCount} step${stepCount === 1 ? '' : 's'} on the map to accomplish: ${intent}`;
  } else {
    whatItDoes = `Runs ${operational.length} connected step${operational.length === 1 ? '' : 's'} to accomplish: ${intent}`;
  }

  return { intent, whatItDoes, actions, source: 'rules' };
}

function compactWorkflowForPrompt(workflow: Workflow): string {
  const ordered = orderedFlowNodes(workflow);
  const lines = ordered.map((n) => {
    const label = getNodeLabel(n);
    const type = getBlockTypeLabel(n.type, n.subtype);
    const detail = getNodePurposeLine(n);
    return `- id=${n.id} [${type}] ${label}${detail && detail !== type ? ` — ${detail}` : ''}`;
  });
  const edgeLines = workflow.edges.map((e) => `${e.from} → ${e.to}`);
  return [
    `Flow name: ${workflow.name}`,
    `Blocks (${ordered.length}):`,
    ...lines,
    `Links (${workflow.edges.length}): ${edgeLines.slice(0, 24).join('; ')}`,
  ].join('\n');
}

/** Serialized map state for Map AI Assistant — GOAL + fulfillment blocks (SSOT). */
export function describeWorkflowForMapAssistant(workflow: Workflow | null | undefined): string {
  return buildGoalCentricMapContext(workflow);
}

export async function generateFlowSummaryAsync(workflow: Workflow): Promise<FlowSummary> {
  const cacheKey = flowSummaryCacheKey(workflow);
  const cached = summaryCache.get(cacheKey);
  if (cached) return cached;

  const fallback = buildRuleBasedFlowSummary(workflow);
  if (workflow.nodes.length === 0) {
    summaryCache.set(cacheKey, fallback);
    return fallback;
  }

  const apiKey = resolveSecret('OPENAI_API_KEY');
  if (!apiKey) {
    summaryCache.set(cacheKey, fallback);
    return fallback;
  }

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: MODEL,
        temperature: 0.25,
        response_format: { type: 'json_object' },
        messages: [
          {
            role: 'system',
            content: `You summarize AutoBlocks Mission flows for builders. Return JSON only with:
- intent: one sentence — the user's desired outcome in plain English
- whatItDoes: 2-3 sentences — how the wired blocks accomplish that outcome (no jargon)
- actions: ordered list of { label, detail } — simplified steps a non-technical user can scan (max 10). Skip purely decorative START unless needed for context.`,
          },
          {
            role: 'user',
            content: compactWorkflowForPrompt(workflow),
          },
        ],
      }),
    });

    if (!response.ok) {
      summaryCache.set(cacheKey, fallback);
      return fallback;
    }

    const data = (await response.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    const content = data.choices?.[0]?.message?.content;
    if (!content) {
      summaryCache.set(cacheKey, fallback);
      return fallback;
    }

    const parsed = flowSummarySchema.parse(JSON.parse(content));
    const ordered = orderedFlowNodes(workflow);
    const typeByLabel = new Map(
      ordered.map((n) => [getNodeLabel(n).toLowerCase(), getBlockTypeLabel(n.type, n.subtype)])
    );

    const summary: FlowSummary = {
      intent: parsed.intent,
      whatItDoes: parsed.whatItDoes,
      actions: parsed.actions.map((a, i) => ({
        order: i + 1,
        label: a.label,
        detail: a.detail,
        blockType: typeByLabel.get(a.label.toLowerCase()) ?? 'Step',
      })),
      source: 'ai',
    };

    summaryCache.set(cacheKey, summary);
    return summary;
  } catch {
    summaryCache.set(cacheKey, fallback);
    return fallback;
  }
}

export function hasFlowSummaryAi(): boolean {
  return hasResolvedSecret('OPENAI_API_KEY');
}

export function invalidateFlowSummaryCache(workflowId: string): void {
  for (const key of summaryCache.keys()) {
    if (key.startsWith(`${workflowId}#`)) summaryCache.delete(key);
  }
}
